import { getCurrentScope } from '@sentry/nuxt';
import { unsubscriptionTypes } from '~/utils/analytics';
import type { NameCookie, TokenCookie } from '@/types/cookies';
import type { Nullable } from '@/types/utils';
import type { NuxtApp } from '#app';

export type Stampcard = {
  active: boolean;
  validTo: string;
  completedAt: null | string;
  stamps: Array<{ earned: boolean }>;
  reward: {
    couponId: string;
    type: string;
    couponType: string;
  };
};

export type Coupon = {
  id: string;
  title: string;
  activated: null | string;
  used: null | string;
  status: string;
  type: string;
};

export type UserState = {
  user: null | Record<string, any>;
  addresses: Array<Record<string, any>>;
  pets: Array<Record<string, any>>;
  token?: null | TokenCookie;
  tealiumAudiences: Array<string>;
  trackerUtm: null | any;
  softLogin: boolean;
  userPetTypes: null | Record<string, any>;
  cryptId: null | string;
  isStoreEditor: boolean;
  editableStores: Array<Record<string, any>>;
  storesSelectables: null | Record<string, any>;
  posAvailabilitySearch: null | Record<string, any>;
  favoriteStoreLocal: null | number | string;
  favoriteStoreLocalPostalCode: null | { postalCode: string };
  passwordUpdateRequired: boolean;
  friendsLightActivated: boolean;
  friendsLightQrCode: null | string;
  friendsLightError: boolean;
  friendsStatus: null | Array<{ membershipType: keyof typeof LOYALTY_TYPES }>;
  stampcards: null | Array<Stampcard>;
  stampcardCurrentCoupon: null | Coupon;
  coupons: null | Array<Coupon>;
  termsOfServiceAccepted: boolean;
  shelterInfo: null | Record<string, any>;
  shelter: null | Record<string, any>;
};

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    user: null,
    addresses: [],
    pets: [],
    token: null,
    tealiumAudiences: [],
    trackerUtm: null,
    softLogin: false,
    userPetTypes: null,
    cryptId: null,
    isStoreEditor: false,
    editableStores: [],
    storesSelectables: null,
    posAvailabilitySearch: null,
    favoriteStoreLocal: null,
    favoriteStoreLocalPostalCode: null,
    passwordUpdateRequired: false,
    friendsLightActivated: false,
    friendsLightQrCode: null,
    friendsLightError: false,
    friendsStatus: null,
    stampcards: null,
    stampcardCurrentCoupon: null,
    coupons: null,
    termsOfServiceAccepted: false,
    shelterInfo: null,
    shelter: null,
  }),
  actions: {
    init: async function (nuxtApp: NuxtApp) {
      logger.debug(`Trying to restore ${logger.inspect('token')} from cookie...`);

      // Token is always provided via token cookie, but we only use it
      // conditionally to prevent leaking private user data in cached responses
      if (!isCacheableRequest()) {
        this.setToken(useCookieToken());
      }

      switch (this.getTokenState) {
        case TOKEN_NOT_FOUND:
          logger.debug('No token found.');
          break;

        case TOKEN_DATE_VALID:
          logger.debug('Valid token found.');
          break;

        case TOKEN_MUST_BE_REFRESHED:
          logger.debug('Token expired with refresh token.');
          await nuxtApp.runWithContext(() => this.refreshToken());
          break;

        case TOKEN_EXPIRED:
          logger.debug('Token expired without refresh token.');
          await nuxtApp.runWithContext(() => useMainStore(nuxtApp.$pinia).resetLogout());
          break;
      }
    },

    refreshToken: async function () {
      logger.debug('Loading refresh token...');
      const { $api, $appBridge, $pinia } = useNuxtApp();
      const mainStore = useMainStore($pinia);

      try {
        const token = $appBridge.isActiveCDC
          ? await $appBridge.actions.requestToken()
          : (await $api.storefront.auth.refresh(this.token?.refresh_token, 'basic')).data;

        logger.debug('Refresh token loaded.');
        this.setToken(token);
      } catch (error: any) {
        logger.error('Error while loading refresh token:', formatErrorLog(error));
        await mainStore.resetLogout();
      }
    },

    // Once with `null` from mainStore in resetLogout action
    // Once for cartStore special case in addToCart action
    // Twice in here, on login and refresh action
    setToken: function (tokenValue?: MaybeRef<Nullable<TokenCookie>>) {
      const { $api, $appBridge, $log } = useNuxtApp();

      // Update pinia store
      this.token = unref(tokenValue);

      if (this.token) {
        $log.debug('Got auth token', $log.inspect({ isAuthenticated: this.getIsAuthenticated }));
      }

      // Update cookie
      useCookieToken().value = this.token;

      // Update API wrapper
      $api.setAccessToken(this.token?.access_token);

      // Update app bridge
      if (this.token) {
        $appBridge.actions.notify('onNewToken', this.token);
      }
    },

    login: async function ({
      username,
      password,
      type,
      grantEcoConsent,
      recaptchaResponse,
      newCustomer = false,
    }: {
      username: string;
      password: string;
      type?: string;
      grantEcoConsent?: boolean;
      recaptchaResponse?: string;
      newCustomer?: boolean;
    }) {
      const nuxtApp = useNuxtApp();
      const { $api, $i18n, $domain, $pinia } = nuxtApp;
      const mainStore = useMainStore($pinia);
      const cartStore = useCartStore($pinia);

      logger.debug('Logging in...');

      const wasSoftLogin = this.softLogin;
      const cdcSynchronousFlows = mainStore.configuration?.cdcSynchronousFlows;
      const baseSite = mainStore.baseStore;

      return new Promise((resolve, reject) =>
        $api.storefront.auth
          .login(username, password, type, cdcSynchronousFlows, baseSite, grantEcoConsent, recaptchaResponse)
          .then(async (response: any) => {
            logger.debug('Login successful.');

            this.setToken(response.data);
            this.softLogin = false;

            await nextTick();

            try {
              await this.loadUser({ id: OCC_USER_ID_CURRENT });

              await Promise.all([
                !wasSoftLogin ? cartStore.merge(newCustomer) : null,
                mainStore.loadAccountNodes(nuxtApp),
                this.loadPetTypes(),
              ]);

              const hasConnectedPBAccount = this.getHasConnectedPBAccount;

              if (hasConnectedPBAccount) {
                await usePaybackStore($pinia).getPaybackSessionToken({
                  host: `https://${$domain}`,
                  forceStrong: false,
                });
              }
            } catch (error: any) {
              if (error?.response?.data?.errors?.[0]?.type === 'WrongBaseStoreError') {
                await mainStore.resetLogout();
              }

              // Rethrow instead of reject to log the actual error in the outer .catch()
              throw error;
            }

            return resolve(true);
          })
          .catch(async (error: any) => {
            logger.error('Error while logging in.', error);

            if (isResponseError(error, 403, PASSWORD_UPDATE_REQUIRED)) {
              logger.debug('User password update required.');
              this.passwordUpdateRequired = true;
              await navigateTo($i18n.t('url.password.update'));
              return;
            }

            return reject(error);
          }),
      );
    },

    logout: async function (hasInvalidToken: boolean) {
      // Skip to prevent unnecessary request and misleading console error.
      // Note: Invalid (or expired) tokens don't need to be revoked, as they are already essentially useless.
      // The request would even fail with "401 Unauthorized", since the **invalid** token would be used as
      // `Authorization` header for the revoke request. Technically speaking we could still revoke the
      // refresh token - but we never did so and all this most likely changes with SSO anyway.
      if (hasInvalidToken || (this.token?.expiry && new Date(this.token.expiry) <= new Date())) {
        return null;
      }

      const { $api } = useNuxtApp();

      logger.debug('Revoking token...');

      return new Promise((resolve) =>
        $api.storefront.auth
          .logout(this.token)
          .then(() => {
            logger.debug('Revoked token successfully.');
            return resolve(true);
          })
          .catch((error: any) => {
            logger.warn('Error while revoking token:', formatErrorLog(error));
            resolve(true);
          }),
      );
    },

    loadUser: async function (payload?: any) {
      const nuxtApp = useNuxtApp();
      const { $api, $errorHandler, $pinia, $appBridge } = nuxtApp;
      const mainStore = useMainStore($pinia);
      const datalayerStore = useDatalayerStore($pinia);

      logger.debug('Loading current user...');

      const { id = this.getType } = payload || {};
      const { skipAnalytics } = payload || false;
      const { preventSoftLoginModal } = payload || false;
      const { analyticsResourceList } = payload || [resourceNames.USER];

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .find(id)
          .then(async (response: Record<string, any>) => {
            logger.debug('Current user loaded.');

            this.user = response.data;

            // Set sentry user
            getCurrentScope().setUser(this.user?.id ? this.user : null);

            if (response.data.isShelterAccount) {
              await nuxtApp.runWithContext(() => this.loadShelterInfo());
            }

            $appBridge.actions.notify('user');

            if (response.data.isStoreEditor && response.data.editableStores) {
              this.editableStores = response.data.editableStores;
            }

            if (!skipAnalytics) {
              await datalayerStore.loadAnalytics(nuxtApp, analyticsResourceList);
            }

            nuxtApp.runWithContext(() => {
              this.setUidCookie();
              this.setNameCookie();
            });

            return resolve(true);
          })
          .catch(async (error: any) => {
            logger.error('Error while loading current user.', error);

            if (isResponseError(error, 401, UNAUTHORIZED_ERROR)) {
              logger.debug('Reset application because of corrupted token.');
              await nuxtApp.runWithContext(() => mainStore.resetLogout());
              return reject(error);
            }

            if (isResponseError(error, 401, INVALID_TOKEN_ERROR)) {
              logger.debug('Reset application because of corrupted token.');
              await nuxtApp.runWithContext(() => mainStore.resetLogout());
            }

            if (isResponseError(error, 401, REQUIRES_HARD_LOGIN_ERROR)) {
              this.softLogin = true;

              if (preventSoftLoginModal) {
                return reject(error);
              }
            }

            nuxtApp.runWithContext(() => $errorHandler.handleErrorModal(error));

            return reject(error);
          }),
      );
    },
    loadShelterInfo: async function () {
      const { $api, $errorHandler, $pinia } = useNuxtApp();
      const mainStore = useMainStore($pinia);

      logger.debug('Loading shelter info...');

      try {
        const res = await $api.hybris.shelters.info(this.getType);
        this.shelterInfo = {
          description: res.data.description || [{ description: '', locale: 'de' }],
          media: res.data.media || {},
          legalForm: res.data.legalForm || '',
          associationRegisterNumber: res.data.associationRegisterNumber || '',
          shelterName: res.data.name || '',
        };
      } catch (error: any) {
        logger.error('Error while loading shelter info.', error);

        if (isResponseError(error, 401, UNAUTHORIZED_ERROR)) {
          logger.debug('Reset application because of corrupted token.');
          await mainStore.resetLogout();
          throw error;
        }

        if (isResponseError(error, 401, REQUIRES_HARD_LOGIN_ERROR)) {
          this.softLogin = true;
        }

        $errorHandler.handleErrorModal(error);
        throw error;
      }
    },
    create: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();

      logger.debug('Creating User...');

      const { language } = useMainStore();

      try {
        const response = await $api.hybris.user.create(data as any, language);
        logger.debug('User successfully created.');
        this.user = response.data;

        const loginData = {
          username: data.uid,
          password: data.password,
          newCustomer: data?.newCustomer,
          recaptchaResponse: undefined as undefined | string,
        };

        if (data.loginRecaptchaResponse) {
          loginData.recaptchaResponse = data.loginRecaptchaResponse;
        }

        await this.login(loginData);
      } catch (error: any) {
        logger.error('Error while creating User.', error);

        if (
          error.response?.data?.errors?.[0]?.message !== 'user.creation.failed' &&
          error.response?.data?.errors?.[0]?.type !== 'AuthenticationServiceError'
        ) {
          $errorHandler.handleErrorModal(error, false);
        }

        throw error;
      }
    },
    update: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();

      logger.debug('Updating user...');

      try {
        await $api.hybris.user.update(this.getType, data);
        logger.debug('User updated.');

        const analyticsResourceList = [resourceNames.USER, resourceNames.CART, resourceNames.WISHLIST];
        await this.loadUser({ analyticsResourceList });
      } catch (error: any) {
        logger.error('Error while updating user.', error);

        $errorHandler.handleErrorModal(error, false);

        throw error;
      }
    },
    patch: async function (payload: { data: Record<string, any>; options?: { skipAnalytics: boolean } }) {
      const { $api, $errorHandler } = useNuxtApp();

      logger.debug('Patching user...');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .patch(this.getType, payload.data)
          .then(async () => {
            logger.debug('User patched.');

            const skipAnalytics = payload.options?.skipAnalytics || false;
            const analyticsResourceList = [resourceNames.USER, resourceNames.CART, resourceNames.WISHLIST];
            await this.loadUser({ skipAnalytics, analyticsResourceList });

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while patching user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    updateShelterInfo: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();

      logger.debug('Updating shelter info...');

      return new Promise((resolve, reject) =>
        $api.hybris.shelters
          .update(this.getType, data)
          .then(async () => {
            logger.debug('Shelter info updated.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating shelter info.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    verifyAddress: async function (data: Record<string, any>) {
      const { $api } = useNuxtApp();
      logger.debug('Verifying address...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .verify(this.getType, data)
          .then((response: any) => {
            if (response.data.decision === 'REVIEW' || response.data.decision === 'REJECT') {
              logger.debug('Address was rejected.');
              return reject(response.data);
            }

            logger.debug('Address verified.');

            return resolve(true);
          })
          .catch((error: any) => {
            if (error?.response?.data?.errors?.[0]?.type !== 'AddressValidationError') {
              logger.error('Error while verifying address.', formatErrorLog(error));
            }

            return resolve(true);
          }),
      );
    },
    loadAddresses: async function () {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Loading addresses of current user...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .all(this.getType)
          .then((response: any) => {
            logger.debug('Addresses loaded.');

            this.addresses = response.data.addresses;
            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while loading addresses of current user.', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    createAddress: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Adding address to user...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .create(this.getType, data)
          .then((response: any) => {
            logger.debug('Address added.');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while adding address to user.', error);

            if (error.response) {
              $errorHandler.handleErrorModal(error, false);
            }

            return reject(error);
          }),
      );
    },
    updateAddress: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Updating address of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .update(this.getType, data.id, data)
          .then(() => {
            logger.debug('Address updated.');

            // Pass through the updated address data so we can assign the resolved value back to the formField
            return resolve(data);
          })
          .catch((error: any) => {
            logger.error('Error while updating address of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    patchAddress: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Patching address of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .patch(this.getType, data.id, data)
          .then(() => {
            logger.debug('Address patched.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while patching address of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    deleteAddress: async function ({ addressId }: { addressId: string }) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Deleting address of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.address
          .delete(this.getType, addressId)
          .then(() => {
            logger.debug('Address deleted.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while deleting address of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    loadPets: async function () {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Loading pets of current user...');

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .all(this.getType)
          .then((response: any) => {
            logger.debug('Pets loaded.');

            this.pets = response.data.pets;
            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while loading pets of current user.', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    createPet: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();

      logger.debug('Adding pet to user...');

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .create(this.getType, data)
          .then((petId) => {
            logger.debug('Pet added.');

            return resolve(petId);
          })
          .catch((error: any) => {
            logger.error('Error while adding pet to user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    updatePet: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Updating pet of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .update(this.getType, data.uid, data)
          .then((response: any) => {
            logger.debug('Pet updated.');

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while updating pet of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    patchPet: async function (data: Record<string, any>) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Patching pet of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .patch(this.getType, data.uid, data)
          .then(() => {
            logger.debug('Pet patched.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating pet of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    deletePet: async function ({ uid }: { uid: string }) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Deleting pet of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .delete(this.getType, uid)
          .then(async () => {
            logger.debug('Pet deleted.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while deleting pet of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    setPet(pet: Record<string, any>) {
      const petStoreState: Array<{ uid: string }> = this.user?.pets?.length ? this.user.pets : this.pets || [];
      const index = petStoreState.findIndex((_pet) => _pet.uid === pet.uid);

      if (index === -1) {
        petStoreState.push(pet as any);
      } else {
        petStoreState.splice(index, 1, pet as any);
      }
    },
    updateEmail: async function ({
      newEmail,
      password,
      recaptchaResponse,
    }: {
      newEmail: string;
      password: string;
      recaptchaResponse: string;
    }) {
      const { $api, $errorHandler, $pinia, $appBridge } = useNuxtApp();
      logger.debug('Updating email of user...');
      const mainStore = useMainStore($pinia);

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .updateEmail(this.getType, newEmail, password, recaptchaResponse, mainStore.language)
          .then(async () => {
            logger.debug('Email updated.');

            await mainStore.resetLogout({ invalidToken: true });
            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating email of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    updateGuestEmail: async function ({ newEmail, oldEmail }: { newEmail: string; oldEmail: string }) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Updating guest email of user...');
      const { language } = useMainStore();

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .updateGuestEmail(this.getType, newEmail, oldEmail, language)
          .then(async () => {
            logger.debug('Guest Email updated.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating guest email of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    updatePassword: async function ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Updating password of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .updatePassword(this.getType, oldPassword, newPassword)
          .then(async () => {
            logger.debug('Password updated.');

            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating password of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    forgotPassword: async function ({
      email,
      redirect,
      recaptchaResponse,
    }: {
      email: string;
      redirect: string;
      recaptchaResponse: string;
    }) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Requesting forgot password token...');
      const { language } = useMainStore();

      return new Promise((resolve, reject) =>
        $api.hybris.forgottenPassword
          .token(email, language, redirect, recaptchaResponse)
          .then(async () => {
            logger.debug('Forgot password token requested.');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while requesting forgot password token.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    resetPassword: async function ({ newPassword, token }: { newPassword: string; token: string }) {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Resetting password...');

      return new Promise((resolve, reject) =>
        $api.hybris.forgottenPassword
          .reset(newPassword, token)
          .then(async () => {
            logger.debug('Password reset.');

            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while resetting password.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    updatePaybackNumber: async function ({ number }: { number: string }) {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Updating payback number of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .updatePaybackNumber(this.getType, number)
          .then((response: any) => {
            logger.debug('Payback number updated.');

            this.user = response.data;
            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while updating payback number of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    deletePaybackNumber: async function () {
      const { $api, $errorHandler, $appBridge } = useNuxtApp();
      logger.debug('Deleting payback number of user...');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .deletePaybackNumber(this.getType)
          .then((response: any) => {
            logger.debug('Payback number deleted.');

            this.user = response.data;
            $appBridge.actions.notify('user');

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while deleting payback number of user.', error);

            $errorHandler.handleErrorModal(error, false);

            return reject(error);
          }),
      );
    },
    setUidCookie: function () {
      useCookie('uid', {
        path: '/',
        maxAge: ONE_YEAR,
        secure: !import.meta.dev,
      }).value = this.getUid;

      if (import.meta.server) {
        updateServerCookie(useRequestEvent(useNuxtApp()), 'uid', this.getUid);
      }
    },
    setNameCookie: function () {
      if (!this.user?.defaultAddress?.lastName) {
        return;
      }

      const cookie = useCookie<NameCookie>('name', {
        path: '/',
        maxAge: ONE_YEAR,
        secure: !import.meta.dev,
      });

      cookie.value = {
        firstName: this.user?.defaultAddress?.firstName || '',
        lastName: this.user?.defaultAddress?.lastName || '',
      };

      if (import.meta.server) {
        updateServerCookie(useRequestEvent(useNuxtApp()), 'name', cookie.value);
      }
    },
    setNameFromCookie: function () {
      const name = unref(useCookie<NameCookie>('name'));

      if (name?.lastName) {
        this.user = {
          ...this.user,
          defaultAddress: {
            ...name,
          },
        };
      }
    },
    setFavoriteStore: async function ({ storeNumber }: { storeNumber: string }) {
      const { $api, $errorHandler } = useNuxtApp();
      if (!this.getIsAuthenticated || this.softLogin) {
        return saveToLocalStorage('favoriteStoreNumber', { storeNumber });
      }

      logger.debug('Setting favorite store...');
      return new Promise((resolve, reject) =>
        $api.hybris.user
          .setFavoriteStore(this.getType, storeNumber)
          .then((response: any) => {
            logger.debug(`Favorite store was set to ${storeNumber}`);

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while setting favorite store', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    setFavoriteStorePostCode: function (postalCode: string) {
      this.favoriteStoreLocalPostalCode = { postalCode };
      return saveToLocalStorage('favoriteStorePostCode', { postalCode });
    },
    setFavoriteStoreLocal: function (storeNumber: string) {
      this.favoriteStoreLocal = storeNumber;
    },
    resetFavoriteStore: async function () {
      deleteFromLocalStorage('favoriteStorePostCode');
      deleteFromLocalStorage('favoriteStoreNumber');
      deleteFromLocalStorage('favoriteStore');
      this.favoriteStoreLocal = null;
      this.favoriteStoreLocalPostalCode = null;

      if (!this.getIsAuthenticated) {
        return null;
      }

      const { $api, $errorHandler, $appBridge } = useNuxtApp();

      logger.debug('Removing favorite store...');
      return new Promise((resolve, reject) =>
        $api.hybris.user
          .resetFavoriteStore(this.getType)
          .then((response: any) => {
            logger.debug('Favorite store information was reset');
            $appBridge.actions.notify('user');

            return resolve(response.data);
          })
          .catch((error: any) => {
            logger.error('Error while resetting favorite store', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    loadPetTypes: async function () {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Loading pet types...');
      const { language } = useMainStore();

      return new Promise((resolve, reject) =>
        $api.hybris.pet
          .types(this.getType, language)
          .then((response: any) => {
            logger.debug('Pets types loaded.');

            this.userPetTypes = response.data;

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while loading pet types', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    loadSmartProductRecos: async function (searchParams: {
      query: string;
      querySorting: string;
      queryLanguage: string;
      queryLimit: number | string;
    }) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Loading Smart product recommendations...');

      const mainStore = useMainStore();
      const abTests = mainStore.abTests.tests;

      return new Promise((resolve, reject) =>
        $api.hybris.product
          .search(
            searchParams.query,
            searchParams.querySorting,
            null,
            null,
            null,
            searchParams.queryLanguage,
            null,
            null,
            'FULL',
            searchParams.queryLimit as number,
            null,
            [],
            abTests,
          )
          .then((response: any) => {
            logger.debug('Smart product recommendations loaded.');

            return resolve(response.data.products);
          })
          .catch((error: any) => {
            logger.error('Error while loading Smart product recommendations', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
    loadBuyAgainProducts: async function (resolveOnError = false) {
      const { $api, $errorHandler } = useNuxtApp();
      logger.debug('Loading buy again products...');
      const userType = this.getType;

      return new Promise((resolve, reject) =>
        $api.hybris.order
          .buyAgainProducts(userType)
          .then((response: any) => {
            logger.debug('Buy again products loaded.');

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while loading buy again products', error);

            if (resolveOnError || error.response.data.errors[0].type === CUSTOMER_NOT_SUFFICIENT_ERROR) {
              return resolve({}); // the response is an object now
            }

            $errorHandler.handleErrorModal(error);
            return reject(error);
          }),
      );
    },
    updateEditableStore: async function (storeToUpdate: Record<string, any>) {
      const { $api } = useNuxtApp();
      const userToken = this.token;
      const userId = this.getCryptId;

      logger.debug('Updating editable store...');
      try {
        await $api.hybris.store.updateStore(userToken, userId, storeToUpdate);
        logger.debug('Store updated successfully...');
      } catch (error: any) {
        logger.error('Error updating editable store');
        logger.error(error);
        throw error;
      }
    },
    loadTealiumAudiences: async function () {
      if (!import.meta.client) {
        return;
      }

      const cookie = useCookie('no-adobe');

      if (cookie?.value) {
        return;
      }

      logger.debug('Loading Tealium audiences...');

      return repeatUntil(() => window?._satellite?.getVar)
        .then(() => {
          this.tealiumAudiences = window._satellite.getVar('TealiumAudiences') || [];
          logger.debug('Tealium audiences loaded:', this.tealiumAudiences);
        })
        .catch((error: any) => {
          this.tealiumAudiences = [];
          if (error) {
            logger.error('Failed to load Tealium audiences:', error);
          }
        });
    },
    loadAbTests: async function () {
      if (!import.meta.client) {
        return;
      }

      const cookie = useCookie('no-adobe');

      if (cookie?.value) {
        return;
      }

      const mainStore = useMainStore();

      logger.debug('Loading AB Tests...');

      return repeatUntil(() => {
        const abTestsCookie = unref(useCookie('FN-UX'));

        if (!abTestsCookie || mainStore.isAppview) {
          mainStore.setAbTests(undefined);
          return;
        }

        const enabledAbTests: Record<string, any> = {};

        for (const [name, enabled] of Object.entries(abTestsCookie)) {
          mainStore.abTests[name] = !!enabled;

          if (mainStore.abTests[name]) {
            enabledAbTests[name] = mainStore.abTests[name];
          }
        }

        mainStore.setAbTests(enabledAbTests);

        return true;
      })
        .then(() => {
          logger.debug('AB tests loaded:', mainStore.abTests);
        })
        .catch((error: any) => {
          if (error) {
            logger.error('Failed to load AB tests:', error);
          }
        });
    },
    setSoftloginState: async function () {
      const { $api, $router, $config } = useNuxtApp();
      const { isAppview } = useMainStore();
      const isProdEnv = $config.public.hybrisBaseUrl.includes('api.os');
      const isTriggerSoftLogin = unref($router.currentRoute).query?.triggerSoftlogin;

      if (isProdEnv || isAppview || isTriggerSoftLogin !== '1' || this.softLogin || !this.getIsAuthenticated) {
        return;
      }

      try {
        await $api.hybrisFn.customer.setSoftLogin(this.user?.uid);
        logger.debug('SoftLogin state set.');
      } catch (error: any) {
        logger.error('Error occurred while trying to set softLogin state.', error);
      }
    },
    activateFriends: async function ({ loyaltyType }: { loyaltyType: keyof typeof LOYALTY_TYPES }) {
      const { $api } = useNuxtApp();
      logger.debug('Activating friends of type:', loyaltyType);
      const userType = this.getType;

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .activateFriends(userType, loyaltyType)
          .then(async (response: any) => {
            logger.debug(`Friends of type ${loyaltyType} activated.`);

            if (loyaltyType === LOYALTY_TYPES.FRIENDS_LIGHT) {
              this.friendsStatus = response.data;
              this.friendsLightActivated = true;
            }

            if (loyaltyType === LOYALTY_TYPES.LITTLE_FRIENDS) {
              this.friendsStatus = response.data;
            }

            return resolve(response);
          })
          .catch((error: any) => {
            logger.error('Error while activating friends of type:', loyaltyType);
            logger.error(error);

            const reactivationMessage = 'Customer already has active loyalty program';
            if (error.response.status === 400 && error.response.data.errors[0].message === reactivationMessage) {
              return resolve(true);
            }

            if (loyaltyType === LOYALTY_TYPES.FRIENDS_LIGHT) {
              this.friendsLightError = true;
            }

            return reject(error);
          }),
      );
    },
    deactivateFriends: async function (loyaltyType: keyof typeof LOYALTY_TYPES) {
      const { $api } = useNuxtApp();
      logger.debug('Deactivating friends of type:', loyaltyType);
      const userType = this.getType;

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .deactivateFriends(userType, loyaltyType)
          .then(async () => {
            logger.debug(`Friends of type ${loyaltyType} deactivated.`);
            await this.loadLoyaltyStatus();
            // neccessary to refresh savingsPlanActive
            await this.loadUser();

            if (loyaltyType in unsubscriptionTypes) {
              const datalayerStore = useDatalayerStore();
              datalayerStore.setUnsubscribedStatus(loyaltyType as keyof typeof unsubscriptionTypes);
            }

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while deactivating friends of type:', loyaltyType);
            logger.error(error);

            return reject(error);
          }),
      );
    },
    retrieveFriendsLightQrCode: async function () {
      const { $api } = useNuxtApp();
      logger.debug('Fetching friends light QR Code...');
      const userType = this.getType;

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .friendsLightQrCode(userType)
          .then((response: any) => {
            if (!response || response.status !== 200 || !response.data) {
              this.friendsLightError = true;
              return reject(new Error(response));
            }

            logger.debug('Friend light QR Code fetched.');
            this.friendsLightQrCode = response.data.code;
            return resolve(response.data.code);
          })
          .catch((error: any) => {
            logger.error('Error while fetching QR Code', error);
            this.friendsLightError = true;

            return reject(error);
          }),
      );
    },
    loadLoyaltyStatus() {
      const { $api } = useNuxtApp();
      logger.debug('Start fetching friends status');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .getLoyaltyStatus(this.getType)
          .then((response: any) => {
            logger.debug('Successfully fetched friends status');
            logger.debug(response.data);
            this.friendsStatus = response.data;

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while fetching friends status', error);
            return reject(error);
          }),
      );
    },
    loadStampcards: async function () {
      const { $api } = useNuxtApp();
      const mainStore = useMainStore();

      logger.debug('Start fetching friends stampcards');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .getStampcards(this.getType, mainStore.language)
          .then((response: any) => {
            logger.debug('Successfully fetched stampcards');
            this.stampcards = response.data.stampcards;

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while fetching stampcards', error);
            return reject(error);
          }),
      );
    },
    loadCoupons: async function () {
      const { $api } = useNuxtApp();
      const mainStore = useMainStore();

      logger.debug('Start fetching coupons');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .getCoupons(this.getType, mainStore.language)
          .then((response: any) => {
            logger.debug('Successfully fetched coupons');
            this.coupons = response.data;

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while fetching coupons', error);
            return reject(error);
          }),
      );
    },
    loadStampcardCurrentCoupon: async function (couponId: string, type: string) {
      const { $api } = useNuxtApp();
      const mainStore = useMainStore();

      logger.debug('Start fetching stampcard current coupon');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .getCoupon(this.getType, couponId, type, mainStore.language)
          .then((response: any) => {
            logger.debug('Successfully fetched stampcard current coupon');
            this.stampcardCurrentCoupon = response.data;

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while fetching stampcard current coupon', error.response.data);
            return reject(error);
          }),
      );
    },
    deleteUserAccount: async function () {
      const { $api, $errorHandler } = useNuxtApp();
      const mainStore = useMainStore();
      const userType = this.getType;

      logger.debug('Deleting user account...');

      return new Promise((resolve, reject) =>
        $api.hybris.user
          .delete(userType, mainStore.language)
          .then(async () => {
            logger.debug('User successfully deleted.');

            await mainStore.resetLogout({ invalidToken: true });

            return resolve(true);
          })
          .catch((error: any) => {
            logger.error('Error while deleting User.', error);

            $errorHandler.handleErrorModal(error);

            return reject(error);
          }),
      );
    },
  },
  getters: {
    getDefaultPaymentMode(state) {
      if (!state.user) return null;
      return state.user.defaultPaymentMode;
    },
    getIsAuthenticated(state) {
      return !!state.token?.refresh_token;
    },
    getType(): any {
      return this.getIsAuthenticated ? OCC_USER_ID_CURRENT : OCC_USER_ID_ANONYMOUS;
    },
    getTokenState(state) {
      return getTokenState(state.token);
    },
    getDisplayUid(state) {
      if (!state.user) return null;
      return state.user.displayUid;
    },
    getUid(state) {
      if (!state.user) return OCC_USER_ID_ANONYMOUS;
      return state.user?.uid;
    },
    getHasAddresses(state) {
      return state.addresses?.length;
    },
    getAddress: (state) => (addressId: string) => {
      return state.addresses?.find((address) => address.id === addressId);
    },
    getDefaultAddress(state) {
      const { user, addresses } = state;

      if (!user) {
        return null;
      }

      return user?.defaultAddress || addresses?.[0] || null;
    },
    getShelterInfo(state) {
      return state.user?.isShelterAccount ? state.shelterInfo : null;
    },
    getBillingAddress(state) {
      const { user, addresses } = state;

      if (!user) {
        return null;
      }

      return addresses?.find((address) => address.billingAddress) || null;
    },
    getDefaultShippingAddress(state) {
      if (!state.user) return null;

      if (state.user.defaultShippingAddress) {
        return state.user.defaultShippingAddress;
      }

      return null;
    },
    getPets(state) {
      if (state.pets?.length) {
        return state.pets;
      }
      if (state.user?.pets?.length) {
        return state.user.pets;
      }
      return [];
    },
    getPetsFilteredByType(state) {
      return state.pets.filter((pet) => pet.type !== PET_TYPE_HORSE && pet.type !== 'OTHERS');
    },
    getBillingAgreement(state) {
      return state.user?.billingAgreement || null;
    },
    getUserFullNameOrUid(state): any {
      // Returns the users full name if set in default address or during checkout,
      // otherwise returns the uid which is the mail address of the user:
      // - `user.uid` can be an email address or `anonymous`, why?
      // - `user.name` can be `Anonymous`, why? Real name is NOT stored in `.name`, but in `.defaultAddress.xxxName`
      // - for soft login, getters.uid is 'anonymous', but $cookies.get('uid') is an email address
      // - for soft login, user now also has a full name stored in defaultAddress.(fir|la)stName and `name` cookie
      // - to make things worse, there is also a user.displayUid
      if (state.user && (this.getUid !== 'anonymous' || state.user.name !== 'Anonymous')) {
        if (this.getDefaultAddress) {
          return `${this.getDefaultAddress.firstName} ${this.getDefaultAddress.lastName}`;
        }
        if (state.user.name) {
          return state.user.name;
        }
        return this.getUid;
      }
      return null;
    },
    getUserFirstNameOrUid(state): any {
      if (state.user && (this.getUid !== 'anonymous' || state.user.name !== 'Anonymous')) {
        if (this.getDefaultAddress) {
          return `${this.getDefaultAddress.firstName}`;
        }
        if (state.user.name) {
          return state.user.name;
        }
        return this.getUid;
      }
      return null;
    },
    getUserFavoriteStore(state) {
      if (!state.user) return null;
      return state.user?.favoriteStore;
    },
    getCryptId(state) {
      if (!state.user) return null;
      return state.user.cryptId;
    },
    getReviewUserToken(state) {
      if (!state.user) return null;
      return state.user.usertoken;
    },
    getHasConnectedPBAccount(state) {
      if (!state.user) return null;
      return state.user.paybackAccountConnected;
    },
    getValidPaybackSessionToken(state) {
      if (!state.user) return null;
      return state.user.validPaybackSessionToken;
    },
    getHasFriendsBenefit(state) {
      if (!state.user) return null;
      return !!state.user?.savingsPlan?.active;
    },
    getHasFriends(state) {
      if (!state.friendsStatus) return null;
      return state.friendsStatus.some((status: any) => status.membershipType === LOYALTY_TYPES.FRIENDS);
    },
    getHasLittleFriends(state) {
      if (!state.friendsStatus) return null;
      return state.friendsStatus.some((status: any) => status.membershipType === LOYALTY_TYPES.LITTLE_FRIENDS);
    },
    getFriendsBenefitExpirationDate(state) {
      if (!state.user) return null;
      return state.user?.savingsPlan?.dateExpire;
    },
    getFriendsLightQrCode(state) {
      if (!state.friendsLightQrCode) {
        return null;
      }

      return state.friendsLightQrCode.toString();
    },
    getUser(state) {
      return state.user;
    },
    getFriendsCompletedStampcardCouponIds(state) {
      return (state.stampcards || [])
        .filter((stampcard) => stampcard.completedAt)
        .map((stampcard) => stampcard.reward.couponId);
    },
    getFriendsActiveStampcard(state) {
      // Backend is sending an empty stampcard as the new active one, as soon as the old stampcard is completed.
      // But we want to display the old completed stampcard, until there is at least one earned stamp on the new one.
      // At some point this logic will hopefully move to BFF.

      const stampcards = state.stampcards || [];

      const activeStampcardWithAtLeastOneEarnedStamp = stampcards.find((stampcard) => {
        return stampcard.active && stampcard.stamps.filter((stamp) => stamp.earned).length;
      });

      if (activeStampcardWithAtLeastOneEarnedStamp) {
        return activeStampcardWithAtLeastOneEarnedStamp;
      }

      // Return the last completed stampcard, if there is no active stampcard with at least one earned stamp
      const sortedCompletedStampcards = stampcards
        .filter((stampcard) => stampcard.completedAt)
        .sort((a, b) => new Date(b.completedAt as string).getTime() - new Date(a.completedAt as string).getTime());

      if (sortedCompletedStampcards[0]) {
        return sortedCompletedStampcards[0];
      }

      // As a fallback, return the active (and empty) stampcard,
      // which is fine for the first ever stampcard for this user
      return stampcards.find((stampcard) => stampcard.active);
    },
    getFriendsStampcardRewardHistory(state): Array<Coupon> {
      return (state.coupons || [])
        .filter((coupon) => {
          // Return only users coupons that are part of completed stampcards, except the active one,
          // that one is shown above as "next reward"
          return (
            this.getFriendsCompletedStampcardCouponIds.includes(coupon.id) &&
            coupon.id !== this.getFriendsActiveStampcard?.reward.couponId
          );
        })
        .map((reward) => ({
          ...reward,
          status: reward.activated ? (reward.used ? 'redeemed' : 'activated') : 'not_activated',
        }))
        .sort((a, b) => {
          // First sort by status, not_activated first
          if (a.status !== b.status) {
            if (a.status === 'not_activated') return -1;
            if (b.status === 'not_activated') return 1;
            if (a.status === 'activated') return -1;
            return 1;
          }

          // Then sort by activated date
          if (a.status === 'activated') {
            return new Date(b.activated as string).getTime() - new Date(a.activated as string).getTime();
          }

          // Finally sort by used date for redeemed coupons
          if (a.status === 'redeemed') {
            return new Date(b.used as string).getTime() - new Date(a.used as string).getTime();
          }

          return 0;
        });
    },
  },
});

export type UserStore = ReturnType<typeof useUserStore>;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
